import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Trash } from "@phosphor-icons/react";
import {
  ResponsiveDialog, ResponsiveDialogContent, ResponsiveDialogHeader,
  ResponsiveDialogTitle, ResponsiveDialogFooter, ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DecimalInput } from "@/components/ui/decimal-input";
import { ChoiceChips } from "@/components/ui/choice-chips";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import CategoryPicker from "@/components/CategoryPicker";
import TransferLinkPanel from "@/components/TransferLinkPanel";
import { sortCategoriesByUsage } from "@/lib/categoryOrder";
import {
  RECURRENCE_FREQUENCIES, availableTypes, isTransferForm, newTransactionForm,
  transactionFormError, transactionFormFrom, transactionPayload, transferPayload,
} from "@/lib/transactionForm";

const TYPE_OPTIONS = {
  expense: { value: "expense", label: "Expense", activeClassName: "bg-expense text-white", testid: "type-expense-btn" },
  income: { value: "income", label: "Income", activeClassName: "bg-income text-black", testid: "type-income-btn" },
  transfer: { value: "transfer", label: "Transfer", activeClassName: "bg-forecast text-black", testid: "type-transfer-btn" },
};

const FREQUENCY_OPTIONS = RECURRENCE_FREQUENCIES.map((frequency) => ({
  value: frequency,
  label: frequency[0].toUpperCase() + frequency.slice(1),
  testid: `txn-frequency-${frequency}`,
}));

export default function TransactionDialog({
  open, onOpenChange, categories, editing, onSave, onSaveTransfer, onDelete,
  accounts = [], accountsById = {}, defaultAccountId,
  onFindTransferCandidates, onLinkTransfer, onUnlinkTransfer,
}) {
  const [form, setForm] = useState(() => newTransactionForm(defaultAccountId));
  const [saving, setSaving] = useState(false);
  const amountRef = useRef(null);
  const setField = (name) => (value) => setForm((current) => ({ ...current, [name]: value }));

  useEffect(() => {
    if (!open) return;
    setForm(editing ? transactionFormFrom(editing) : newTransactionForm(defaultAccountId));
    // The default account only seeds a new form; a scope change while the
    // form is open must not overwrite the user's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  // Opened straight from a URL, the form can beat the account list here; fill
  // the default in once it arrives, without replacing anything already chosen.
  useEffect(() => {
    if (!open || editing || !defaultAccountId) return;
    setForm((current) => ({
      ...current,
      account_id: current.account_id || defaultAccountId,
      from_account_id: current.from_account_id || defaultAccountId,
    }));
  }, [open, editing, defaultAccountId]);

  const multipleAccounts = accounts.length > 1;
  const transfer = isTransferForm(form);
  const types = availableTypes(editing, multipleAccounts).map((type) => TYPE_OPTIONS[type]);

  // Most-used first, so the categories a user reaches for are at the top.
  const catList = useMemo(
    () => sortCategoriesByUsage(form.type === "income" ? categories.income : categories.expense),
    [categories, form.type],
  );

  const chooseType = (type) => setForm((current) => ({ ...current, type, category: "" }));

  /**
   * Amount is the one field every transaction needs and the only one with no
   * usable default, so it takes focus when the form opens. Existing text is
   * selected so editing an amount is a retype rather than a cursor hunt.
   */
  const focusAmount = (event) => {
    event.preventDefault();
    // One frame later: the form is still animating in, and a focus set
    // mid-transition gets dropped on the way.
    requestAnimationFrame(() => {
      amountRef.current?.focus();
      amountRef.current?.select();
    });
  };

  const submit = async () => {
    const error = transactionFormError(form);
    if (error) return toast.error(error);
    setSaving(true);
    try {
      await save();
      toast.success(editing ? "Transaction updated" : "Transaction added");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save transaction");
    } finally {
      setSaving(false);
    }
  };

  const save = () => (transfer
    ? onSaveTransfer(editing?.transfer_id || null, transferPayload(form))
    : onSave(editing, transactionPayload(form)));

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        className="bg-card border-border sm:max-w-md"
        data-testid="transaction-dialog"
        onOpenAutoFocus={focusAmount}
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="font-heading tracking-tight">
            {editing ? "Edit transaction" : "New transaction"}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="text-muted-foreground">
            Enter the amount, category and date for your transaction.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="space-y-4">
          {types.length > 1 && (
            <ChoiceChips
              label="Transaction type"
              variant="segmented"
              options={types}
              value={form.type}
              onValueChange={chooseType}
            />
          )}

          <AmountField ref={amountRef} value={form.amount} onChange={setField("amount")} type={form.type} />

          <Field label="Date">
            <Input
              data-testid="txn-date-input"
              type="date"
              value={form.date}
              onChange={(e) => setField("date")(e.target.value)}
              className="bg-background touch:h-11"
            />
          </Field>

          {transfer ? (
            <TransferAccountFields form={form} accounts={accounts} setField={setField} />
          ) : multipleAccounts && (
            <AccountField label="Account" value={form.account_id} accounts={accounts} onChange={setField("account_id")} />
          )}

          {!transfer && (
            <CategoryField
              categories={catList}
              value={form.category}
              kind={form.type}
              onChange={setField("category")}
            />
          )}

          <Field label="Description">
            <Textarea
              data-testid="txn-description-input"
              value={form.description}
              onChange={(e) => setField("description")(e.target.value)}
              placeholder="Optional note"
              className="bg-background resize-none h-16"
            />
          </Field>

          <RecurrenceFields
            recurring={form.recurring}
            frequency={form.recurrence_frequency}
            onRecurringChange={setField("recurring")}
            onFrequencyChange={setField("recurrence_frequency")}
          />

          {editing && multipleAccounts && (
            <TransferLinkPanel
              transaction={editing}
              accountsById={accountsById}
              onFindCandidates={onFindTransferCandidates}
              onLink={onLinkTransfer}
              onUnlink={onUnlinkTransfer}
              onDone={() => onOpenChange(false)}
            />
          )}

          {editing && onDelete && (
            <DeleteButton transfer={Boolean(editing.transfer_id)} onClick={() => onDelete(editing)} />
          )}
        </div>

        <ResponsiveDialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="txn-cancel-btn">Cancel</Button>
          <Button
            data-testid="txn-save-btn"
            onClick={submit}
            disabled={saving}
            className="bg-income text-black hover:bg-income/90 font-semibold"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Add transaction"}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const AMOUNT_COLOR = { expense: "text-expense", income: "text-income", transfer: "text-forecast" };

/**
 * The one field every entry needs, so it is the biggest thing on the form.
 * "Done" on the keypad puts the keyboard away: everything after the amount is
 * a tap, and a keyboard left up would cover exactly those controls.
 */
function AmountField({ ref, value, onChange, type }) {
  return (
    <Field label="Amount">
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-heading text-2xl font-bold text-muted-foreground">
          €
        </span>
        <DecimalInput
          ref={ref}
          data-testid="txn-amount-input"
          value={value}
          onValueChange={onChange}
          enterKeyHint="done"
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          placeholder="0.00"
          className={`h-14 bg-background pl-9 font-heading text-3xl font-bold tabular-nums md:mouse:text-3xl ${AMOUNT_COLOR[type]}`}
        />
      </div>
    </Field>
  );
}

function AccountField({ label, value, accounts, onChange, testid = "txn-account" }) {
  const options = accounts.map((account) => ({
    value: account.id,
    label: account.name,
    testid: `${testid}-${account.id}`,
  }));
  return (
    <Field label={label}>
      <ChoiceChips label={label} options={options} value={value} onValueChange={onChange} />
    </Field>
  );
}

function TransferAccountFields({ form, accounts, setField }) {
  return (
    <div className="space-y-4">
      <AccountField
        label="From" value={form.from_account_id} accounts={accounts}
        onChange={setField("from_account_id")} testid="txn-from-account"
      />
      <AccountField
        label="To" value={form.to_account_id} accounts={accounts}
        onChange={setField("to_account_id")} testid="txn-to-account"
      />
    </div>
  );
}

function CategoryField({ categories, value, kind, onChange }) {
  return (
    <Field label="Category">
      <CategoryPicker categories={categories} value={value} kind={kind} onChange={onChange} />
      {!categories.length && (
        <p className="text-xs text-muted-foreground mt-1.5">
          No {kind} categories yet —{" "}
          <Link to="/categories" className="text-income underline underline-offset-2">create one</Link>.
        </p>
      )}
    </Field>
  );
}

function RecurrenceFields({ recurring, frequency, onRecurringChange, onFrequencyChange }) {
  return (
    <>
      {/* The whole row is the target, not just the switch at its edge. */}
      <label className="flex min-h-14 cursor-pointer items-center justify-between gap-3 rounded-md border border-border p-3">
        <span>
          <span className="block text-sm font-medium">Recurring</span>
          <span className="block text-xs text-muted-foreground">Repeats on a schedule</span>
        </span>
        <Switch data-testid="txn-recurring-switch" checked={recurring} onCheckedChange={onRecurringChange} />
      </label>

      {recurring && (
        <Field label="Frequency">
          <ChoiceChips
            label="Frequency"
            variant="segmented"
            options={FREQUENCY_OPTIONS}
            value={frequency}
            onValueChange={onFrequencyChange}
          />
        </Field>
      )}
    </>
  );
}

function DeleteButton({ transfer, onClick }) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      data-testid="txn-delete-btn"
      className="w-full gap-2 text-expense hover:bg-expense/10 hover:text-expense touch:h-11"
    >
      <Trash size={16} /> {transfer ? "Delete transfer" : "Delete transaction"}
    </Button>
  );
}
